<script setup lang="ts">
import { useConsentStore } from "~~/stores/consentStore";
import { useAuthStore } from "~~/stores/authStore";
import { termsLabel } from "~~/app/brands";

const brand = useBrand();
const consent = useConsentStore();
const auth = useAuthStore();
// Tryb katalogowy: kolumna „Strefa klienta" dotyczy wyłącznie zakupów
// (status zamówienia, zwroty, konto), więc w showcase'ie nie ma czego opisywać.
const { sellingEnabled } = useCatalogOnly();
// Gdy wewnętrzne zwroty są wyłączone (prod), stopka prowadzi do zakładki
// /zwroty z banerem odstąpienia od umowy (Dyrektywa UE 2023/2673) — nie
// bezpośrednio do zewnętrznego formularza nadania przesyłki.
const { internalEnabled: internalReturns } = useReturns();

// Zalogowani → historia zamówień; goście → publiczne śledzenie zamówienia.
// Render zależny od auth tylko po stronie klienta (ClientOnly w template) —
// trasy SWR współdzielą HTML między userami, więc SSR/cache zawsze pokazuje
// wariant gościa, a po hydratacji link sam koryguje się dla zalogowanych.
const orderStatusLink = computed(() =>
  auth.isLoggedIn ? "/account/orders" : "/sledzenie-zamowienia",
);
</script>

<template>
  <footer class="site-footer">
    <div class="footer-inner container">
      <!-- Top: logo + columns -->
      <div class="footer-top">
        <!-- Brand -->
        <div class="footer-brand">
          <!-- Stopka ma ciemne tło, więc bierzemy wersję odwróconą logotypu,
               jeśli marka ją ma. Bez tego czarny napis „proles" znikał na
               grafitowym tle — widoczny zostawał sam turkusowy znak. -->
          <img
            :src="brand.logo.srcInverted || brand.logo.src"
            :alt="brand.name"
            width="129"
            height="24"
            class="footer-logo"
          />
          <!-- Każdy wiersz warunkowo: marka bez kompletu danych firmy (sklep
               `gadzety` do czasu ich otrzymania) renderowała samo „, Polska"
               i pusty odnośnik mailto:. -->
          <address class="footer-address">
            <template v-if="brand.footer.address">
              {{ brand.footer.address }},<br />
            </template>
            <template v-if="brand.footer.postalCode || brand.footer.city">
              {{ brand.footer.postalCode }} {{ brand.footer.city }}<br />
            </template>
            {{ brand.footer.country }}<br /><br />
            <template v-if="brand.footer.email">
              <a v-mailto="brand.footer.email">{{
                brand.footer.email
              }}</a
              ><br />
            </template>
            <template v-if="brand.footer.phone"> tel. {{ brand.footer.phone }} </template>
          </address>
          <!-- Bez ani jednego profilu nagłówek „Social media" wisiał nad pustką. -->
          <div
            v-if="brand.social.tiktok || brand.social.instagram || brand.social.facebook"
            class="footer-social"
          >
            <p class="footer-social__label">Social media</p>
            <div class="footer-social__icons">
              <a
                v-if="brand.social.tiktok"
                :href="brand.social.tiktok"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="TikTok"
                class="footer-social__link"
              >
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                >
                  <path
                    d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-2.88 2.5 2.89 2.89 0 0 1-2.89-2.89 2.89 2.89 0 0 1 2.89-2.89c.28 0 .54.04.79.1V9.01a6.28 6.28 0 0 0-.79-.05 6.34 6.34 0 0 0-6.34 6.34 6.34 6.34 0 0 0 6.34 6.34 6.34 6.34 0 0 0 6.33-6.34V8.19a8.19 8.19 0 0 0 4.79 1.53V6.27a4.85 4.85 0 0 1-1.02-.42z"
                  />
                </svg>
              </a>
              <a
                v-if="brand.social.instagram"
                :href="brand.social.instagram"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Instagram"
                class="footer-social__link"
              >
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                >
                  <path
                    d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 1 0 0 12.324 6.162 6.162 0 0 0 0-12.324zM12 16a4 4 0 1 1 0-8 4 4 0 0 1 0 8zm6.406-11.845a1.44 1.44 0 1 0 0 2.881 1.44 1.44 0 0 0 0-2.881z"
                  />
                </svg>
              </a>
              <a
                v-if="brand.social.facebook"
                :href="brand.social.facebook"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Facebook"
                class="footer-social__link"
              >
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                >
                  <path
                    d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"
                  />
                </svg>
              </a>
            </div>
          </div>
        </div>

        <!-- Strefa klienta — tylko w sklepie sprzedającym -->
        <div v-if="sellingEnabled" class="footer-col">
          <p class="footer-col__title">Strefa klienta</p>
          <ul class="footer-col__list">
            <li>
              <ClientOnly>
                <NuxtLink :to="orderStatusLink">Status zamówienia</NuxtLink>
                <template #fallback>
                  <NuxtLink to="/sledzenie-zamowienia"
                    >Status zamówienia</NuxtLink
                  >
                </template>
              </ClientOnly>
            </li>
            <li>
              <NuxtLink v-if="internalReturns" to="/account/returns"
                >Zwroty i reklamacje</NuxtLink
              >
              <NuxtLink v-else to="/zwroty"
                >Zwroty i odstąpienie od umowy</NuxtLink
              >
            </li>
            <li>
              <NuxtLink to="/tabele-rozmiarow">Tabele rozmiarów</NuxtLink>
            </li>
            <li><NuxtLink to="/account">Moje konto</NuxtLink></li>
            <li class="footer-col__install">
              <ClientOnly>
                <InstallAppButton />
              </ClientOnly>
            </li>
          </ul>
        </div>

        <!-- Firma -->
        <div class="footer-col">
          <p class="footer-col__title">Firma</p>
          <ul class="footer-col__list">
            <li><NuxtLink to="/about">O nas</NuxtLink></li>
            <li><NuxtLink to="/contact">Kontakt</NuxtLink></li>
            <!-- Blog ukryty do czasu uruchomienia sekcji (Asana 2026-06-16).
                 Osobny serwis WordPress pod /blog — twardy link (pełne
                 przeładowanie), bo SPA-routing Nuxta nie obsłuży tej trasy.
            <li><a href="/blog">Blog</a></li>
            -->
          </ul>
        </div>

        <!-- Regulaminy -->
        <div class="footer-col">
          <p class="footer-col__title">Regulaminy</p>
          <ul class="footer-col__list">
            <li>
              <NuxtLink to="/polityka-prywatnosci"
                >Polityka Prywatności</NuxtLink
              >
            </li>
            <li>
              <NuxtLink to="/regulamin-sklepu">{{ termsLabel(brand) }}</NuxtLink>
            </li>
            <!-- Bez narzędzi analitycznych i marketingowych nie ma czym
                 zarządzać — link otwierałby panel pustych zgód. -->
            <li v-if="consent.hasTrackingToConsent">
              <button
                type="button"
                class="footer-link-btn"
                data-testid="footer-cookie-settings"
                @click="consent.reopen()"
              >
                Ustawienia Cookies
              </button>
            </li>
          </ul>
        </div>

        <!-- Kategorie. Bez linków kolumna znika — proles miał `links: []`
             i nagłówek „Kategorie" wisiał nad pustą listą, choć config
             obiecywał, że pusta lista chowa kolumnę. -->
        <div
          v-if="brand.footer.links.length"
          class="footer-col footer-col--kategorie"
        >
          <p class="footer-col__title">Kategorie</p>
          <ul class="footer-col__list">
            <li
              v-for="link in brand.footer.links"
              :key="link.href + link.label"
            >
              <NuxtLink :to="link.href">{{ link.label }}</NuxtLink>
            </li>
          </ul>
        </div>
      </div>

      <!-- Bottom: legal -->
      <div class="footer-bottom">
        <p>
          © 2026 {{ brand.footer.copyrightName }}, Wszelkie prawa zastrzeżone
        </p>
        <p>Created by {{ brand.footer.createdBy }}</p>
      </div>
    </div>
  </footer>
</template>

<style scoped>
.site-footer {
  /* Ciemna powierzchnia marki: proles ma ciemny teal ze swoich grafik, inne
     marki tokenu nie definiują i zostają przy czerni/grafitcie. */
  background: var(--color-surface-dark, var(--color-black));
  color: var(--color-white);
  padding: 60px 0 0;
}

.footer-inner {
  display: flex;
  flex-direction: column;
  gap: 40px;
}

/* ── Top ── */
.footer-top {
  display: grid;
  grid-template-columns: 1.2fr repeat(4, 1fr);
  gap: 40px;
  align-items: start;
}

/* ── Brand column ── */
.footer-logo {
  display: block;
  margin: 0 0 24px;
  height: 1.5rem;
  width: auto;
  /* logo.svg jest czarne — na ciemnej stopce wymuszamy biel */
  filter: brightness(0) invert(1);
}

/* Proles Concept ma własną wersję odwróconą logotypu (biały napis, znak
   w kolorze marki), więc filtr jest zbędny — wybieliłby też turkusowy znak.
   Wysokość dobrana osobno, bo ten logotyp jest mniej rozciągnięty
   w poziomie (~3,2:1 wobec ~5,4:1). 30.10.2026 plik przycięto do rysunku
   (wcześniej 56% wysokości to był margines): 22 px samego logo to ten sam
   widoczny rozmiar, co dawniej 40 px obrazka z marginesem. */
[data-brand="proles"] .footer-logo {
  filter: none;
  height: 1.375rem;
}

/* DISCANTO: jasna wersja logo od grafika (srcInverted), więc bez filtra. Pasek
   8,8:1 wymiarowany szerokością, jak w nagłówku. */
[data-brand="discanto"] .footer-logo {
  filter: none;
  width: 151px;
  height: auto;
  aspect-ratio: 799 / 91;
}

.footer-address {
  margin: 0 0 24px;
  font-size: 1rem;
  font-weight: var(--font-weight-light);
  font-style: normal;
  line-height: 1.6;
  color: var(--color-white);
}

.footer-address a {
  text-decoration: underline;
  color: var(--color-white);
}

.footer-social__label {
  margin: 0 0 12px;
  font-size: 1rem;
  font-weight: var(--font-weight-bold);
  color: var(--color-white);
}

.footer-social__icons {
  display: flex;
  gap: 12px;
  align-items: center;
}

.footer-social__link {
  color: var(--color-white);
  opacity: 0.8;
  transition: opacity var(--transition-fast);
}

.footer-social__link:hover {
  opacity: 1;
}

/* ── Link columns ── */
.footer-col__title {
  margin: 0 0 20px;
  font-size: 1rem;
  font-weight: var(--font-weight-bold);
  color: var(--color-white);
  line-height: 1;
}

.footer-col__list {
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.footer-col__list a {
  font-size: 1rem;
  font-weight: var(--font-weight-light);
  color: var(--color-white);
  line-height: 1;
  transition: opacity var(--transition-fast);
}

.footer-col__list a:hover {
  opacity: 0.7;
}

/* Proles ma czternaście działów — w jednej kolumnie stopka urosłaby o ~450 px
   ponad sąsiednie kolumny z dwoma linkami. Dwie kolumny tekstu (kolejność
   z góry na dół, jak w menu), a na szerokim ekranie kolumna zajmuje też wolne
   piąte pole siatki: proles nie ma „Strefy klienta". */
[data-brand="proles"] .footer-col--kategorie .footer-col__list {
  display: block;
  columns: 2;
  column-gap: 24px;
}

[data-brand="proles"] .footer-col--kategorie .footer-col__list li {
  break-inside: avoid;
  margin-bottom: 16px;
}

@media (min-width: 1201px) {
  [data-brand="proles"] .footer-col--kategorie {
    grid-column: span 2;
  }
}

.footer-link-btn {
  padding: 0;
  border: none;
  background: transparent;
  font-size: 1rem;
  font-weight: var(--font-weight-light);
  color: var(--color-white);
  line-height: 1;
  cursor: pointer;
  text-align: left;
  transition: opacity var(--transition-fast);
}

.footer-link-btn:hover {
  opacity: 0.7;
}

/* ── Bottom ── */
.footer-bottom {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 16px 0;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  font-size: 1rem;
  font-weight: var(--font-weight-light);
  color: var(--color-white);
  white-space: nowrap;
  flex-wrap: wrap;
}

.footer-bottom p {
  margin: 0;
}

@media (max-width: 1200px) {
  .footer-top {
    grid-template-columns: 1fr 1fr;
  }
}

@media (max-width: 960px) {
  .footer-top {
    grid-template-columns: 1fr;
  }

  .footer-bottom {
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
    white-space: normal;
  }
}
</style>
